import type { Dispense, TransactionDetail } from '@/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';

import { sumAssetUnits } from '@/helpers/redemption';

import {
  Banner,
  DARK_CARD_BG,
  DarkStatColumn,
  DetailRow,
  ERROR_LIGHT_BG,
  SUCCESS_LIGHT_BG,
  SectionLabel,
  WARNING_LIGHT_BG,
} from './statusUi';

type TransactionDispenseTabProps = {
  transaction: TransactionDetail;
};

type SaleAudit = TransactionDetail['sale_audits'][number];

const getDispenseStatus = (partial: boolean, completed: boolean) => {
  if (completed && !partial) return 'success';
  if (completed && partial) return 'partial';
  return 'failed';
};

const getDispenseAuditTimes = (saleAudits: SaleAudit[], index: number) => {
  const dispensingIndexes = saleAudits
    .map((audit, i) => (audit.stage === 'dispensing' ? i : -1))
    .filter((i) => i !== -1);
  const auditIndex = dispensingIndexes[index];
  if (auditIndex == null) return null;

  const startedAt = saleAudits[auditIndex].updated_at;
  const resolvedAt = saleAudits[auditIndex + 1]?.updated_at ?? null;
  return { startedAt, resolvedAt };
};

const DispenseStatusBanner = ({
  partial,
  completed,
  expected,
  dispensed,
}: {
  partial: boolean;
  completed: boolean;
  expected: number;
  dispensed: number;
}) => {
  const status = getDispenseStatus(partial, completed);

  if (status === 'success') {
    return (
      <Banner
        icon={<CheckCircleIcon fontSize="inherit" />}
        color="#2E7D32"
        backgroundColor={SUCCESS_LIGHT_BG}
        title="Fully Dispensed"
        description={`All ${expected} Goldback${expected === 1 ? '' : 's'} were dispensed successfully.`}
      />
    );
  }

  if (status === 'partial') {
    return (
      <Banner
        icon={<WarningAmberIcon fontSize="inherit" />}
        color="#B45309"
        backgroundColor={WARNING_LIGHT_BG}
        title="Partial Dispense"
        description={`Expected ${expected}, only ${dispensed} Goldback${dispensed === 1 ? '' : 's'} dispensed.`}
      />
    );
  }

  return (
    <Banner
      icon={<ErrorOutlineIcon fontSize="inherit" />}
      color="#C62828"
      backgroundColor={ERROR_LIGHT_BG}
      title="Dispense Failed"
      description={`Expected ${expected}, ${dispensed} Goldback${dispensed === 1 ? '' : 's'} dispensed.`}
    />
  );
};

const DispenseAttempt = ({
  dispense,
  index,
  total,
  saleAudits,
}: { dispense: Dispense; index: number; total: number; saleAudits: SaleAudit[] }) => {
  const activeCassettes = dispense.cassette_results.filter(
    (c) => c.requested_for_dispensing > 0 || c.dispensed_count > 0 || c.total_rejected_notes > 0,
  );

  const totalExpected = sumAssetUnits(dispense.expected_bills);
  const totalDispensed = sumAssetUnits(dispense.successful_bills);
  const totalRejected = sumAssetUnits(dispense.rejected_bills);
  const auditTimes = getDispenseAuditTimes(saleAudits, index);

  return (
    <Box>
      {total > 1 && <SectionLabel>Attempt {index + 1}</SectionLabel>}

      <DispenseStatusBanner
        partial={dispense.partial}
        completed={dispense.completed}
        expected={totalExpected}
        dispensed={totalDispensed}
      />

      <Box sx={{ p: 3, borderRadius: '12px', backgroundColor: DARK_CARD_BG, mb: 3 }}>
        <Stack direction="row" justifyContent="space-around" alignItems="center">
          <DarkStatColumn label="Expected" value={`${totalExpected} GB`} />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          <DarkStatColumn
            label="Dispensed"
            value={`${totalDispensed} GB`}
            color={totalDispensed === totalExpected ? '#66BB6A' : '#FFB74D'}
          />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          <DarkStatColumn
            label="Rejected"
            value={`${totalRejected} GB`}
            color={totalRejected > 0 ? '#EF5350' : '#fff'}
          />
        </Stack>
      </Box>

      {auditTimes && (
        <Box
          sx={{ backgroundColor: 'neutral.100', borderRadius: '12px', px: 2, mb: 3 }}
          data-test={`dispense-attempt-${index}-timestamps`}
        >
          <DetailRow label="Started" value={new Date(auditTimes.startedAt).toLocaleString()} />
          <DetailRow
            label="Resolved"
            value={auditTimes.resolvedAt ? new Date(auditTimes.resolvedAt).toLocaleString() : 'N/A'}
            last
          />
        </Box>
      )}

      {activeCassettes.length > 0 && (
        <>
          <SectionLabel>Cassette Details</SectionLabel>
          <Stack spacing={1.5}>
            {activeCassettes.map((cassette) => {
              const rejectionEntries = Object.entries(cassette.rejection_reasons).filter(
                ([, count]) => count > 0,
              );

              return (
                <Box
                  key={cassette.cassette_slot}
                  sx={{
                    px: 2,
                    pt: 1.5,
                    pb: rejectionEntries.length === 0 ? 1.5 : 0,
                    backgroundColor: 'neutral.100',
                    borderRadius: '12px',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.5}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {cassette.display_name ?? cassette.cassette_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cassette.cassette_name} · Slot {cassette.cassette_slot}
                    </Typography>
                  </Stack>

                  <DetailRow label="Requested" value={cassette.requested_for_dispensing} />
                  <DetailRow
                    label="Dispensed"
                    value={cassette.dispensed_count}
                    valueColor={
                      cassette.dispensed_count === cassette.requested_for_dispensing
                        ? 'success.main'
                        : 'warning.main'
                    }
                  />
                  <DetailRow
                    label="Rejected"
                    value={cassette.total_rejected_notes}
                    valueColor={cassette.total_rejected_notes > 0 ? 'error.main' : 'text.primary'}
                    last={rejectionEntries.length === 0}
                  />

                  {rejectionEntries.length > 0 && (
                    <Box sx={{ pt: 1.5, pb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" display="block" mb={1}>
                        Rejection reasons
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {rejectionEntries.map(([reason, count]) => (
                          <Chip
                            key={reason}
                            label={`${reason.replaceAll('_', ' ')}: ${count}`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
};

const TransactionDispenseTab = ({ transaction }: TransactionDispenseTabProps) => {
  const dispenses = transaction.dispenses ?? [];

  if (!dispenses.length) {
    return (
      <Stack py={6} textAlign="center">
        <Typography variant="h5" color="text.secondary">
          No dispense data available
        </Typography>
      </Stack>
    );
  }

  return (
    <Box pt={2}>
      <Stack spacing={3}>
        {dispenses.map((dispense, index) => (
          <DispenseAttempt
            key={dispense.id}
            dispense={dispense}
            index={index}
            total={dispenses.length}
            saleAudits={transaction.sale_audits ?? []}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default TransactionDispenseTab;
